import type { CanonicalExam } from '../core/model/canonical.ts';
import type { ValidationReport } from '../core/validator/report.ts';
import type { ImportRequest, ImportResponse, ImportStage } from './importProtocol.ts';

export type ImportResult =
  | { readonly ok: true; readonly exam: CanonicalExam; readonly report: ValidationReport }
  | { readonly ok: false; readonly report: ValidationReport }
  | { readonly ok: false; readonly crashed: true; readonly message: string };

export interface ImportOptions {
  readonly appVersion: string;
  readonly onProgress?: (stage: ImportStage) => void;
}

/**
 * Runs an import, in a worker where one is available.
 *
 * The fallback is not defensive programming for its own sake: workers are unavailable in some
 * embedded browsers and in a few privacy configurations, and an app whose only feature is opening
 * a file should not simply stop working there. It runs the identical pipeline, just on the main
 * thread, so behaviour cannot drift between the two paths.
 */
export async function importExamFile(file: File, options: ImportOptions): Promise<ImportResult> {
  if (typeof Worker === 'undefined') {
    return runFallback(file, options);
  }

  try {
    return await importInWorker(file, options);
  } catch {
    return runFallback(file, options);
  }
}

/**
 * Loads the fallback only when it is needed.
 *
 * A static import here would be the same code twice on every visit: the pipeline is already inside
 * the worker bundle, and importing it from the client copies the precompiled schema validators —
 * the largest thing in the repository — into the main bundle as well. Almost nobody takes this
 * path, so almost nobody should pay for it.
 */
async function runFallback(file: File, options: ImportOptions): Promise<ImportResult> {
  const { importOnMainThread } = await import('./importFallback.ts');
  return importOnMainThread(file, options);
}

function importInWorker(file: File, options: ImportOptions): Promise<ImportResult> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./import.worker.ts', import.meta.url), { type: 'module' });

    const finish = (result: ImportResult): void => {
      worker.terminate();
      resolve(result);
    };

    worker.onmessage = (event: MessageEvent<ImportResponse>) => {
      const message = event.data;
      switch (message.type) {
        case 'progress':
          options.onProgress?.(message.stage);
          break;
        case 'done':
          finish({ ok: true, exam: message.exam, report: message.report });
          break;
        case 'failed':
          finish({ ok: false, report: message.report });
          break;
        case 'crashed':
          finish({ ok: false, crashed: true, message: message.message });
          break;
      }
    };

    // Rejecting rather than resolving sends both of these to the main-thread fallback: the worker
    // may simply have failed to load, or have failed to hand back what it produced, and the file
    // itself could be perfectly fine either way.
    const abandon = (reason: string): void => {
      worker.terminate();
      reject(new Error(reason));
    };

    worker.onerror = (event) => {
      abandon(event.message);
    };

    /**
     * A reply that could not be delivered.
     *
     * Rare, and the reason it is handled at all: without this the promise never settles, and an
     * import that failed in this one way would leave the screen saying "checking the file" forever
     * — the only failure mode in the whole application with no way out but a reload.
     */
    worker.onmessageerror = () => {
      abandon('the import worker sent a message that could not be read');
    };

    const request: ImportRequest = {
      type: 'import',
      file,
      appVersion: options.appVersion,
      generatedAt: new Date().toISOString(),
    };
    worker.postMessage(request);
  });
}
